'use client';
import * as React from 'react';
import { useActionState } from 'react';
import { useTranslations } from 'next-intl';
import { Button, FormError, Icon } from './ui';
import { AflorarMark } from './aflorar-mark';
import { OnboardingSteps } from './onboarding-steps';
import { AuthPanel } from './auth-panel';
import { ConsentCheckbox } from './consent-checkbox';
import { Input } from '@/components/ui/field';
import { PhoneInput } from '@/components/ui/phone-input';
import { Callout } from '@/components/ui/callout';
import { submitAuth, type ActionState } from '@/lib/actions';
import { browserTimezone } from '@/lib/timezones';
import { createClient } from '@/lib/supabase/client';
import { PhoneAccountActivation } from './phone-account-activation';
import { PhoneMfaChallenge } from './phone-mfa-challenge';
import { PhoneSignupVerification } from './phone-signup-verification';
import { AuthIdentifierInput, PHONE_GLASS, type AuthIdentifierKind } from './auth-identifier-input';

type AuthMode = 'entrar' | 'criar' | 'recuperar' | 'activate-phone';

export function AuthGate({
  defaultMode = 'entrar',
  next = '',
  notice,
  successNotice,
  defaultRole = 'patient',
  refCode = '',
  phoneLoginEnabled = false,
  resumeMfa = false,
  signupAllowed = false,
  onboarding = false,
  footer,
  clinicName,
}: {
  defaultMode?: AuthMode;
  next?: string;
  notice?: string;
  successNotice?: string;
  defaultRole?: 'patient' | 'professional';
  /** Referral code from a share link (?ref=) — forwarded through signup. */
  refCode?: string;
  phoneLoginEnabled?: boolean;
  resumeMfa?: boolean;
  /** Invite only: "Criar conta" shows only for someone arriving from a clinic invitation. */
  signupAllowed?: boolean;
  /** A new studio's owner: sign-up is step 1 of 2 (owner decision, 2026-11-03). */
  onboarding?: boolean;
  /** Rendered by the (server) page: the scenery with the Lanuq credit. */
  footer?: React.ReactNode;
  /** The patient's clinic, shown in place of the product wordmark. */
  clinicName?: string;
}) {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const [mode, setMode] = React.useState<AuthMode>(defaultMode);
  // Invite only: the invitation decides the account type, so there is no picker (owner, 2026-11-03).
  const role = defaultRole;
  const [identifierKind, setIdentifierKind] = React.useState<AuthIdentifierKind>('email');
  const [consent, setConsent] = React.useState(false);
  const [passwordValue, setPasswordValue] = React.useState('');
  const [confirmValue, setConfirmValue] = React.useState('');
  const [complete, setComplete] = React.useState(false);
  // Said once the second field is as long as the first, not on every keystroke.
  const mismatch = mode === 'criar' && confirmValue.length >= passwordValue.length && confirmValue.length > 0 && confirmValue !== passwordValue;
  // Capture the browser's zone for the new account's default (handle_new_user reads it).
  // Set post-mount so SSR and the first client render agree (no hydration mismatch).
  const [timezone, setTimezone] = React.useState('America/Sao_Paulo');
  React.useEffect(() => setTimezone(browserTimezone()), []);
  const [state, formAction, pending] = useActionState<ActionState, FormData>(submitAuth, undefined);
  // The sign-in error marks both fields and goes away as soon as she types again.
  const [editedSinceError, setEditedSinceError] = React.useState(false);
  React.useEffect(() => setEditedSinceError(false), [state]);
  const signInError = mode === 'entrar' && Boolean(state?.error) && !editedSinceError;
  // On the sky (the sign-in page) the fields are glass pills and the button is raised.
  const glass = Boolean(footer);
  const fieldVariant = glass ? ('glass' as const) : ('default' as const);
  const showMfa = resumeMfa || !!state?.mfaRequired;
  const showPhoneSignup = !!state?.phoneSignupRequired && !!state.phone;

  // Password recovery — client-side so the PKCE code_verifier cookie lands in
  // THIS browser (the emailed link must be opened where it was requested).
  const [recState, setRecState] = React.useState<'idle' | 'pending' | 'sent'>('idle');
  const [recError, setRecError] = React.useState<string | null>(null);
  const submitRecovery = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get('email') ?? '');
    setRecState('pending');
    setRecError(null);
    const supabase = createClient();
    const resetHref = next ? `/reset-password?next=${encodeURIComponent(next)}` : '/reset-password';
    const callback = new URL('/auth/callback', window.location.origin);
    callback.searchParams.set('next', resetHref);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: callback.toString(),
    });
    if (error) {
      setRecError(error.message);
      setRecState('idle');
    } else {
      setRecState('sent'); // generic — never reveals whether the account exists
    }
  };

  return (
    <AuthPanel
      // One centred column with the logo (owner, 2026-11-02): no brand panel, no greeting.
      footer={footer}
      scenery={Boolean(footer)}
      header={
        <>
          {clinicName ? (
            <p className="mb-7 text-center text-2xl font-semibold tracking-tight text-foreground">{clinicName}</p>
          ) : (
            <AflorarMark className={footer ? 'mb-7 flex justify-center' : undefined} />
          )}
          {onboarding && mode === 'criar' && <OnboardingSteps current={1} />}
        </>
      }
      title={
        showPhoneSignup
          ? t('phoneSignupTitle')
          : showMfa
            ? t('mfaTitle')
            : mode === 'entrar'
              ? undefined
              : mode === 'criar'
                ? onboarding
                  ? undefined // the step rail already names it
                  : t('createTitle')
                : mode === 'activate-phone'
                  ? t('phoneActivationTitle')
                  : t('recoverTitle')
      }
      subtitle={
        showPhoneSignup
          ? t('phoneSignupSubtitle')
          : showMfa
            ? t('mfaSubtitle')
            : mode === 'entrar'
              ? undefined
              : mode === 'criar' && onboarding
                ? undefined
                : mode === 'criar'
                ? // A professional is building a page, not booking a consultation: the
                  // patient line ("comece a agendar agora") read as if they were one.
                  role === 'professional'
                  ? t('createSubtitleProfessional')
                  : t('createSubtitle')
                : mode === 'activate-phone'
                  ? t('phoneActivationSubtitle')
                  : t('recoverSubtitle')
      }
    >
      {showPhoneSignup ? (
        <PhoneSignupVerification
          challengeId={state.challengeId!}
          password={passwordValue}
          next={state.next ?? ''}
        />
      ) : showMfa ? (
        <PhoneMfaChallenge
          challengeId={state?.challengeId ?? ''}
          next={state?.next || next || '/app'}
        />
      ) : mode === 'activate-phone' ? (
        <PhoneAccountActivation />
      ) : mode === 'recuperar' ? (
        recState === 'sent' ? (
          <Callout
            role="status"
            tone="success"
            icon="checkmark-circle-02"
            className="rounded-md text-[0.8125rem]"
          >
            {t('recoverySent')}
          </Callout>
        ) : (
          <form onSubmit={submitRecovery} className="flex flex-col gap-3">
            {notice && <FormError>{notice}</FormError>}
            <Input
              name="email"
              icon="mail-01"
              type="email"
              placeholder={t('emailPlaceholder')}
              aria-label={t('emailPlaceholder')}
              required
            />
            {recError && <FormError>{recError}</FormError>}
            <Button
              type="submit"
              variant="default"
              block
              size="lg"
              loading={recState === 'pending'}
            >
              {t('sendRecovery')}
            </Button>
          </form>
        )
      ) : (
        <form
          action={formAction}
          className="flex flex-col gap-3"
          // Criar conta stays off until every required field is valid (owner, 2026-11-03).
          onInput={(event) => {
            setComplete(event.currentTarget.checkValidity());
            setEditedSinceError(true);
          }}
          onChange={(event) => setComplete(event.currentTarget.checkValidity())}
        >
          <input type="hidden" name="mode" value={mode} />
          <input type="hidden" name="next" value={next} />

          {successNotice && (
            <Callout role="status" tone="success" icon="checkmark-circle-02">
              {successNotice}
            </Callout>
          )}

          {mode === 'criar' && (
            <>
              <Input
                name="full_name"
                variant={fieldVariant}
                icon="user"
                type="text"
                placeholder={t('fullNamePlaceholder')}
                aria-label={t('fullNamePlaceholder')}
                required
              />
              <input type="hidden" name="role" value={role} />
              <input type="hidden" name="timezone" value={timezone} />
              {refCode && <input type="hidden" name="ref" value={refCode} />}
            </>
          )}

          {/* Creating an account always comes from an invitation, which already knows the
              e-mail (owner, 2026-11-03): no address to type, the server uses the invitation's. */}
          {mode === 'criar' ? null : phoneLoginEnabled ? (
            <AuthIdentifierInput
              name="identifier"
              placeholder={t('identifierPlaceholder')}
              phonePlaceholder={t('phonePlaceholder')}
              required
              invalid={signInError}
              glass={glass}
              onKindChange={setIdentifierKind}
            />
          ) : (
            <Input
              name="identifier"
              variant={fieldVariant}
              icon="mail-01"
              type="email"
              placeholder={t('emailPlaceholder')}
              aria-label={t('emailPlaceholder')}
              aria-invalid={signInError || undefined}
              required
            />
          )}
          {mode === 'criar' && (!phoneLoginEnabled || identifierKind === 'email') && (
            <div>
              <PhoneInput
                containerClassName={glass ? PHONE_GLASS : undefined}
                name="phone"
                placeholder={t('phonePlaceholder')}
                aria-label={t('phonePlaceholder')}
                required
              />
              <p className="mt-1.5 text-[0.78125rem] text-muted-foreground">
                {role === 'professional' ? t('phoneHintProfessional') : t('phoneHint')}
              </p>
            </div>
          )}
          {/* Optional, patients only: digital prescriptions need it on the document
              (docs/investigations/mevo-prescriptions.md). The professional's CPF is asked in
              onboarding; a patient who skips it here is asked once, at the first prescription. */}
          {mode === 'criar' && role === 'patient' && (!phoneLoginEnabled || identifierKind === 'email') && (
            <div>
              <Input
                name="cpf"
                variant={fieldVariant}
                icon="id"
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
                placeholder={t('cpfPlaceholder')}
                aria-label={t('cpfPlaceholder')}
              />
              <p className="mt-1.5 text-[0.78125rem] text-muted-foreground">{t('cpfHint')}</p>
            </div>
          )}
          <Input
            name="password"
            variant={fieldVariant}
            icon="lock-key"
            type="password"
            value={passwordValue}
            onChange={(event) => setPasswordValue(event.target.value)}
            placeholder={t('passwordPlaceholder')}
            aria-label={t('passwordPlaceholder')}
            aria-invalid={signInError || undefined}
            aria-describedby={signInError ? 'auth-error' : undefined}
            required
          />
          {mode === 'criar' && (
            <Input
              name="password_confirm"
              variant={fieldVariant}
              icon="lock-key"
              type="password"
              value={confirmValue}
              onChange={(event) => setConfirmValue(event.target.value)}
              placeholder={t('passwordConfirmPlaceholder')}
              aria-label={t('passwordConfirmPlaceholder')}
              aria-invalid={mismatch || undefined}
              required
            />
          )}
          {mismatch && <FormError>{t('passwordMismatch')}</FormError>}


          {mode === 'criar' && (
            <ConsentCheckbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          )}

          {/* Sign-in: the fields turn red and one quiet line says why, under them (owner,
              2026-11-03); other modes keep the form error. */}
          {state?.error && mode === 'entrar' ? (
            signInError && (
              <p id="auth-error" role="alert" className="-mt-1 flex items-center gap-1.5 text-[0.8125rem] font-medium text-destructive">
                <Icon name="alert-circle" size={15} className="shrink-0" />
                {state.error}
              </p>
            )
          ) : (
            state?.error && <FormError>{state.error}</FormError>
          )}

          <Button
            type="submit"
            variant={glass ? 'raised' : 'default'}
            block
            size="lg"
            loading={pending}
            className={glass ? 'h-12 rounded-full' : undefined}
            disabled={mode === 'criar' && (!complete || !consent || passwordValue.length < 8 || confirmValue !== passwordValue)}
          >
            {mode === 'entrar' ? tc('actions.signIn') : tc('actions.createAccount')}
          </Button>
          {/* Under the action, as a quiet dotted shortcut (owner, 2026-11-03). */}
          {mode === 'entrar' && (
            <Button
              variant="unstyled"
              onClick={() => setMode('recuperar')}
              className="mx-auto mt-1 text-[0.8125rem] text-muted-foreground underline decoration-dotted decoration-2 underline-offset-4 hover:text-foreground"
            >
              {t('forgotPassword')}
            </Button>
          )}
        </form>
      )}

      {/* No Google or other social sign-in (owner, 2026-11-02): accounts are invite only. */}

      {!showMfa && !showPhoneSignup && mode !== 'activate-phone' && (mode !== 'entrar' || signupAllowed) && (
        <p className="mt-5 text-center text-[0.8125rem] text-muted-foreground">
          {mode === 'entrar'
            ? t('noAccountPrompt')
            : mode === 'criar'
              ? t('hasAccountPrompt')
              : t('rememberedPrompt')}{' '}
          <Button
            variant="link"
            onClick={() => setMode(mode === 'entrar' ? 'criar' : 'entrar')}
            className="font-semibold text-medlux-soft-foreground"
          >
            {mode === 'entrar' ? tc('actions.createAccount') : tc('actions.signIn')}
          </Button>
        </p>
      )}

    </AuthPanel>
  );
}
